<template>
  <div class="clinic-scope invite-page">
    <div class="invite-card">
      <img
        class="vm-logo"
        src="../../assets/images/logo-videomedi.png"
        alt="Videomedi"
      />

      <div v-if="state === 'loading'" class="invite-muted">
        Carregando convite…
      </div>

      <div v-else-if="state === 'invalid'" class="invite-muted">
        <h2 class="invite-title">Convite inválido</h2>
        <p>Este convite não existe ou já foi utilizado.</p>
      </div>

      <template v-else>
        <h2 class="invite-title">Você foi convidado(a)</h2>
        <p v-if="doctorWithAccount" class="invite-muted">
          {{ invite.name }}, você já tem uma conta na Videomedi. Entre na sua
          conta para ver e aceitar o convite da
          <strong>{{ invite.clinic_name }}</strong>
          no menu Clínicas.
        </p>
        <p v-else-if="existingAccount" class="invite-muted">
          {{ invite.name }}, você já tem uma conta na Videomedi. Entre com a
          senha dela para acessar
          <strong>{{ invite.clinic_name }}</strong>
          {{ roleSuffix }}.
        </p>
        <p v-else class="invite-muted">
          {{ invite.name }}, complete seu cadastro para acessar
          <strong>{{ invite.clinic_name }}</strong>
          {{ roleSuffix }}.
        </p>

        <!-- Médico: condições definidas pela clínica no convite -->
        <div v-if="isDoctor && hasContract" class="invite-contract">
          <p v-if="invite.transfer_pct" class="invite-contract__line">
            Repasse acordado:
            <strong>{{ invite.transfer_pct }}%</strong>
            do valor de cada consulta para você (a clínica retém
            {{ 100 - invite.transfer_pct }}%).
          </p>
          <p v-if="invite.unit_name" class="invite-contract__line">
            Unidade principal:
            <strong>{{ invite.unit_name }}</strong>
          </p>
          <p class="invite-contract__note">
            Ao aceitar o convite, você concorda com essas condições.
          </p>
        </div>

        <!-- Médico com conta aceita no próprio painel, não por aqui -->
        <router-link
          v-if="doctorWithAccount"
          data-test="login-to-panel"
          class="btn btn--primary invite-panel-link"
          :to="{ name: 'Login', query: { redirectFrom: '/medico/clinicas' } }"
        >
          Entrar e ver convite
        </router-link>

        <!-- Erros sem campo correspondente (ex.: e-mail do convite já em uso) -->
        <div
          v-if="!doctorWithAccount && generalErrors.length"
          class="invite-banner"
          role="alert"
        >
          <p v-for="msg in generalErrors" :key="msg">{{ msg }}</p>
        </div>

        <!-- E-mail do convite (imutável) -->
        <div v-if="!doctorWithAccount" class="field">
          <span class="label">E-mail</span>
          <input class="input" type="email" :value="invite.email" disabled />
          <span class="field-hint">
            Definido pelo convite e não pode ser alterado.
          </span>
        </div>

        <form
          v-if="!doctorWithAccount"
          class="invite-form"
          novalidate
          @submit.prevent="submit"
        >
          <template v-if="!existingAccount">
            <label class="field">
              <span class="label">CPF{{ optionalSuffix }}</span>
              <input
                ref="cpf"
                v-model="form.cpf"
                class="input"
                :class="{ 'input--invalid': errorsFor('cpf').length }"
                inputmode="numeric"
                placeholder="000.000.000-00"
                aria-label="CPF"
                @input="onCpfInput"
                @blur="validateField('cpf')"
              />
              <span
                v-for="msg in errorsFor('cpf')"
                :key="msg"
                class="field-error"
              >
                {{ msg }}
              </span>
            </label>

            <label class="field">
              <span class="label">Telefone{{ optionalSuffix }}</span>
              <input
                ref="phoneNumber"
                v-model="form.phoneNumber"
                class="input"
                :class="{ 'input--invalid': errorsFor('phoneNumber').length }"
                inputmode="tel"
                placeholder="(00) 00000-0000"
                aria-label="Telefone"
                @input="onPhoneInput"
                @blur="validateField('phoneNumber')"
              />
              <span
                v-for="msg in errorsFor('phoneNumber')"
                :key="msg"
                class="field-error"
              >
                {{ msg }}
              </span>
            </label>

            <label class="field">
              <span class="label">Data de nascimento{{ optionalSuffix }}</span>
              <input
                ref="birthday"
                v-model="form.birthday"
                v-mask="'##/##/####'"
                type="tel"
                class="input"
                :class="{ 'input--invalid': errorsFor('birthday').length }"
                placeholder="00/00/0000"
                aria-label="Data de nascimento"
                @blur="validateField('birthday')"
              />
              <span
                v-for="msg in errorsFor('birthday')"
                :key="msg"
                class="field-error"
              >
                {{ msg }}
              </span>
            </label>

            <!-- Médico: dados profissionais (antes eram preenchidos pela clínica) -->
            <template v-if="isDoctor">
              <label class="field">
                <span class="label">Conselho profissional</span>
                <select
                  ref="councilId"
                  v-model="form.councilId"
                  class="input"
                  :class="{ 'input--invalid': errorsFor('councilId').length }"
                  aria-label="Conselho profissional"
                  @change="onCouncilChange"
                >
                  <option value="" disabled>Selecione…</option>
                  <option v-for="c in councils" :key="c.id" :value="c.id">
                    {{ c.name }}
                  </option>
                </select>
                <span
                  v-for="msg in errorsFor('councilId')"
                  :key="msg"
                  class="field-error"
                >
                  {{ msg }}
                </span>
              </label>

              <label class="field">
                <span class="label">Número do registro</span>
                <input
                  ref="councilNumber"
                  v-model="form.councilNumber"
                  class="input"
                  :class="{
                    'input--invalid': errorsFor('councilNumber').length
                  }"
                  :inputmode="isCrm ? 'numeric' : 'text'"
                  :placeholder="isCrm ? '000000' : 'Ex.: 06/123456'"
                  aria-label="Número do registro"
                  @input="clearServer('councilNumber')"
                  @blur="validateField('councilNumber')"
                />
                <span
                  v-for="msg in errorsFor('councilNumber')"
                  :key="msg"
                  class="field-error"
                >
                  {{ msg }}
                </span>
              </label>

              <label class="field">
                <span class="label">UF do conselho</span>
                <select
                  ref="councilUf"
                  v-model="form.councilUf"
                  class="input"
                  :class="{ 'input--invalid': errorsFor('councilUf').length }"
                  aria-label="UF do conselho"
                  @change="validateField('councilUf')"
                >
                  <option value="" disabled>Selecione…</option>
                  <option v-for="uf in ufs" :key="uf" :value="uf">
                    {{ uf }}
                  </option>
                </select>
                <span
                  v-for="msg in errorsFor('councilUf')"
                  :key="msg"
                  class="field-error"
                >
                  {{ msg }}
                </span>
              </label>

              <label class="field">
                <span class="label">Especialidade principal</span>
                <select
                  ref="specialty"
                  v-model="form.specialty"
                  class="input"
                  :class="{ 'input--invalid': errorsFor('specialty').length }"
                  aria-label="Especialidade principal"
                  @change="validateField('specialty')"
                >
                  <option value="" disabled>Selecione…</option>
                  <option v-for="s in specialties" :key="s.id" :value="s.name">
                    {{ s.name }}
                  </option>
                </select>
                <span
                  v-for="msg in errorsFor('specialty')"
                  :key="msg"
                  class="field-error"
                >
                  {{ msg }}
                </span>
              </label>

              <!-- RQE só existe para médico (conselho CRM) -->
              <label v-if="isCrm" class="field">
                <span class="label">
                  RQE
                  <span class="label-optional">(opcional)</span>
                </span>
                <input
                  ref="rqe"
                  v-model="form.rqe"
                  class="input"
                  inputmode="numeric"
                  placeholder="Registro de Qualificação de Especialista"
                  aria-label="RQE"
                />
              </label>

              <label class="field">
                <span class="label">Sobre você</span>
                <textarea
                  ref="shortBio"
                  v-model="form.shortBio"
                  class="input invite-textarea"
                  :class="{ 'input--invalid': errorsFor('shortBio').length }"
                  rows="3"
                  placeholder="Formação, experiência e áreas de atuação (aparece no seu perfil público)."
                  aria-label="Sobre você"
                  @input="clearServer('shortBio')"
                  @blur="validateField('shortBio')"
                />
                <span
                  v-for="msg in errorsFor('shortBio')"
                  :key="msg"
                  class="field-error"
                >
                  {{ msg }}
                </span>
              </label>
            </template>
          </template>

          <label class="field">
            <span class="label">Senha</span>
            <BaseInput
              ref="password"
              type="password"
              show-password-toggle
              aria-label="Senha"
              v-model="form.password"
              @input="validateField('password')"
            />
            <ul v-if="!existingAccount" class="pw-rules" aria-hidden="false">
              <li
                v-for="rule in passwordRules"
                :key="rule.key"
                :class="{ ok: rule.test(form.password) }"
              >
                <span class="pw-mark">
                  {{ rule.test(form.password) ? "✓" : "•" }}
                </span>
                {{ rule.label }}
              </li>
            </ul>
            <span
              v-for="msg in serverErrorsFor('password')"
              :key="msg"
              class="field-error"
            >
              {{ msg }}
            </span>
          </label>

          <label v-if="!existingAccount" class="field">
            <span class="label">Confirmar senha</span>
            <BaseInput
              ref="passwordConfirmation"
              type="password"
              show-password-toggle
              aria-label="Confirmar senha"
              v-model="form.passwordConfirmation"
              @input="validateField('passwordConfirmation')"
            />
            <span
              v-for="msg in errorsFor('passwordConfirmation')"
              :key="msg"
              class="field-error"
            >
              {{ msg }}
            </span>
          </label>

          <button class="btn btn--primary" type="submit" :disabled="submitting">
            {{ submitting ? "Enviando…" : "Aceitar convite" }}
          </button>
        </form>
      </template>
    </div>
  </div>
</template>

<script>
import { ClinicInvite } from "@/services/clinica";
import { Speciality } from "@/services/speciality";
import { Council } from "@/services/council";
import { UFS } from "@/utils/constants";
import { parseRecordInvalid } from "@/utils/apiErrors";
import {
  PASSWORD_RULES,
  failingPasswordRules,
  isPasswordValid
} from "@/utils/passwordRules";
import { validCalendarDate, notFutureDate } from "@/utils/validations/birthday";

const ROLE_LABELS = { admin: "Administrador", atendimento: "Atendimento" };

// chave do backend (nome do model) → campo do formulário
const API_FIELD_MAP = {
  cpf: "cpf",
  phone_number: "phoneNumber",
  birthday: "birthday",
  password: "password",
  password_confirmation: "passwordConfirmation",
  // médico: erros do doctor_property (nested attributes)
  "doctor_property.council": "councilId",
  "doctor_property.council_number": "councilNumber",
  "doctor_property.council_uf": "councilUf",
  "doctor_property.medical_specialty": "specialty",
  "doctor_property.short_bio": "shortBio"
  // email / name não têm input (vêm do convite) → caem em `general`
};

// Conta existente: só a senha da conta (as regras de senha nova não se aplicam).
const EXISTING_ACCOUNT_FIELD_ORDER = ["password"];

const FIELD_ORDER = [
  "cpf",
  "phoneNumber",
  "birthday",
  "password",
  "passwordConfirmation"
];

// Médico: dados profissionais entram depois do nascimento (ordem do form).
// RQE é opcional, então não entra na validação.
const DOCTOR_FIELD_ORDER = [
  "cpf",
  "phoneNumber",
  "birthday",
  "councilId",
  "councilNumber",
  "councilUf",
  "specialty",
  "shortBio",
  "password",
  "passwordConfirmation"
];

function onlyDigits(v) {
  return (v || "").replace(/\D/g, "");
}

// Validação de CPF (dígitos verificadores) — pré-checagem no front; a validade
// definitiva e a unicidade continuam no backend.
function isValidCpf(raw) {
  const cpf = onlyDigits(raw);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  const calc = factor => {
    let sum = 0;
    for (let i = 0; i < factor - 1; i++) {
      sum += parseInt(cpf[i], 10) * (factor - i);
    }
    const mod = (sum * 10) % 11;
    return mod === 10 ? 0 : mod;
  };
  return calc(10) === +cpf[9] && calc(11) === +cpf[10];
}

function maskCpf(v) {
  const d = onlyDigits(v).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

// Mensagem simples da API (`render json: { error: "..." }`).
function apiErrorMessage(error) {
  const body = error && error.response && error.response.data;
  return body && typeof body.error === "string" ? body.error : "";
}

function maskPhone(v) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 10) {
    return d.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return d.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
}

export default {
  name: "ClinicInviteAccept",
  data() {
    return {
      state: "loading",
      invite: {},
      submitting: false,
      passwordRules: PASSWORD_RULES,
      ufs: UFS,
      // Especialidades e conselhos reais (endpoints públicos) — só médico.
      specialties: [],
      councils: [],
      form: {
        cpf: "",
        phoneNumber: "",
        birthday: "",
        councilId: "",
        councilNumber: "",
        councilUf: "",
        specialty: "",
        rqe: "",
        shortBio: "",
        password: "",
        passwordConfirmation: ""
      },
      // erros de validação client-side, por campo
      clientErrors: {},
      // erros vindos do backend, por campo
      serverErrors: {},
      generalErrors: []
    };
  },
  created() {
    this.fetch();
    if (this.isDoctor) {
      this.fetchSpecialties();
      this.fetchCouncils();
    }
  },
  methods: {
    async fetch() {
      try {
        const { data } = await ClinicInvite.show(this.kind, this.token);
        this.invite = data;
        this.prefill(data);
        this.state = "ready";
      } catch (e) {
        this.state = "invalid";
      }
    },
    async fetchSpecialties() {
      try {
        const { data } = await Speciality.all();
        this.specialties = data || [];
      } catch (e) {
        this.specialties = [];
      }
    },
    async fetchCouncils() {
      try {
        const { data } = await Council.all();
        // Só conselhos reais (com sigla); descarta registros sem `code`.
        this.councils = (data || []).filter(c => c.code);
      } catch (e) {
        this.councils = [];
      }
    },
    onCouncilChange() {
      // RQE não se aplica fora do CRM: limpa se trocar de conselho.
      if (!this.isCrm) this.form.rqe = "";
      this.validateField("councilId");
      if (this.form.councilNumber) this.validateField("councilNumber");
    },
    // Pré-preenche o que o convite já traz: telefone informado pela clínica e,
    // em convites antigos, os dados profissionais (o médico pode corrigir).
    prefill(data) {
      if (data.phone_number) {
        this.form.phoneNumber = maskPhone(data.phone_number);
      }
      if (!this.isDoctor) return;
      if (data.council_id) this.form.councilId = data.council_id;
      if (data.council_number) this.form.councilNumber = data.council_number;
      if (data.council_uf) this.form.councilUf = data.council_uf;
      if (data.specialty) this.form.specialty = data.specialty;
    },
    onCpfInput() {
      this.form.cpf = maskCpf(this.form.cpf);
      this.clearServer("cpf");
    },
    onPhoneInput() {
      this.form.phoneNumber = maskPhone(this.form.phoneNumber);
      this.clearServer("phoneNumber");
    },
    // Mensagens visíveis de um campo = client + server (client primeiro).
    errorsFor(field) {
      return [
        ...(this.clientErrors[field] || []),
        ...(this.serverErrors[field] || [])
      ];
    },
    serverErrorsFor(field) {
      return this.serverErrors[field] || [];
    },
    // limpa o erro de backend do campo assim que o usuário edita
    clearServer(field) {
      if (this.serverErrors[field]) {
        this.$delete(this.serverErrors, field);
      }
    },
    validateField(field) {
      this.clearServer(field);
      const errors = this.computeFieldErrors(field);
      if (errors.length) {
        this.$set(this.clientErrors, field, errors);
      } else if (this.clientErrors[field]) {
        this.$delete(this.clientErrors, field);
      }
      return errors.length === 0;
    },
    computeFieldErrors(field) {
      const f = this.form;
      // Conta de equipe (US-C12): CPF, telefone e nascimento são opcionais;
      // se preenchidos, valem as mesmas regras.
      const optional =
        !this.isDoctor && ["cpf", "phoneNumber", "birthday"].includes(field);
      const empty = {
        cpf: !onlyDigits(f.cpf),
        phoneNumber: !onlyDigits(f.phoneNumber),
        birthday: !(f.birthday || "").trim()
      }[field];
      if (optional && empty) return [];
      switch (field) {
        case "cpf":
          if (!onlyDigits(f.cpf)) return ["Informe o CPF."];
          if (onlyDigits(f.cpf).length !== 11)
            return ["O CPF deve ter 11 dígitos."];
          if (!isValidCpf(f.cpf)) return ["CPF inválido."];
          return [];
        case "phoneNumber":
          if (!onlyDigits(f.phoneNumber)) return ["Informe o telefone."];
          if (onlyDigits(f.phoneNumber).length < 10)
            return ["Telefone incompleto."];
          return [];
        case "councilId":
          if (!f.councilId) return ["Selecione o conselho profissional."];
          return [];
        case "councilNumber":
          if (!f.councilNumber.trim()) return ["Informe o número do registro."];
          // CRM é só número; outros conselhos usam formatos como 06/123456.
          if (this.isCrm && !/^\d+$/.test(f.councilNumber.trim()))
            return ["Use apenas números no CRM."];
          return [];
        case "councilUf":
          if (!f.councilUf) return ["Selecione a UF do conselho."];
          return [];
        case "specialty":
          if (!f.specialty) return ["Selecione a especialidade principal."];
          return [];
        case "shortBio":
          if (!f.shortBio.trim()) return ["Conte um pouco sobre você."];
          return [];
        case "birthday":
          if (!f.birthday) return ["Informe a data de nascimento."];
          if (!validCalendarDate(f.birthday))
            return ["Data de nascimento inválida."];
          if (!notFutureDate(f.birthday))
            return ["Data futura não pode ser enviada."];
          return [];
        case "password":
          if (!f.password) return ["Informe a senha."];
          if (this.existingAccount) return [];
          if (!isPasswordValid(f.password)) {
            return failingPasswordRules(f.password).map(
              r => `A senha precisa de: ${r.label.toLowerCase()}.`
            );
          }
          return [];
        case "passwordConfirmation":
          if (!f.passwordConfirmation) return ["Confirme a senha."];
          if (f.passwordConfirmation !== f.password)
            return ["As senhas não conferem."];
          return [];
        default:
          return [];
      }
    },
    validateAll() {
      let ok = true;
      this.fieldOrder.forEach(field => {
        if (!this.validateField(field)) ok = false;
      });
      return ok;
    },
    focusFirstError() {
      const first = this.fieldOrder.find(f => this.errorsFor(f).length);
      const target = this.$refs[first];
      if (!target) return;
      // password/passwordConfirmation usam BaseInput (ref = instância do
      // componente); os demais campos são <input> nativos (ref = elemento).
      const el = target.$el
        ? target.$el.querySelector("input, select, textarea")
        : target;
      if (el && typeof el.focus === "function") {
        el.focus();
      }
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    async submit() {
      if (this.doctorWithAccount) return;
      if (this.submitting) return;
      this.generalErrors = [];
      this.serverErrors = {};

      if (!this.validateAll()) {
        this.$toast.error(
          "Revise os campos destacados para concluir o cadastro."
        );
        this.focusFirstError();
        return;
      }

      this.submitting = true;
      try {
        const { data } = await ClinicInvite.accept(
          this.kind,
          this.token,
          this.form,
          { existingAccount: this.existingAccount }
        );
        // Auto-login: o interceptor do axios salva os headers de auth da resposta.
        this.$store.commit("auth/SET_LOGGED_IN", true);
        await this.$store.dispatch("user/showUser", data.id);
        this.$toast.success("Cadastro concluído");
        if (this.isDoctor) {
          // Médico recém-cadastrado ainda não tem agenda nem documentos: vai
          // para o guia de onboarding, como no login (resolvePostLoginRedirect).
          this.$store.commit("auth/SET_USER_GUIDE", true);
          this.$router.push({ name: "GuidePage" });
        } else {
          this.$router.push({ name: "ClinicDashboard" });
        }
      } catch (e) {
        this.handleAcceptError(e);
      } finally {
        this.submitting = false;
      }
    },
    handleAcceptError(error) {
      const parsed = parseRecordInvalid(error, API_FIELD_MAP);
      if (parsed.isValidation) {
        this.serverErrors = parsed.fieldErrors;
        this.generalErrors = parsed.general;
        this.$toast.error(
          "Revise os campos destacados para concluir o cadastro."
        );
        this.$nextTick(() => this.focusFirstError());
      } else if (apiErrorMessage(error)) {
        // 422 com mensagem da API (ex.: { error: "Especialidade não encontrada." })
        this.generalErrors = [apiErrorMessage(error)];
        this.$toast.error(apiErrorMessage(error));
      } else {
        this.$toast.error(
          "Não foi possível concluir o cadastro. Tente novamente."
        );
      }
    }
  },
  computed: {
    kind() {
      return this.$route.meta.kind || "staff";
    },
    token() {
      return this.$route.params.token;
    },
    isDoctor() {
      return this.kind === "doctor";
    },
    selectedCouncil() {
      return this.councils.find(c => c.id === this.form.councilId) || null;
    },
    // RQE só aparece (e só é enviado) quando o conselho é CRM.
    isCrm() {
      return Boolean(
        this.selectedCouncil && this.selectedCouncil.code === "CRM"
      );
    },
    existingAccount() {
      return this.invite.existing_account === true;
    },
    // Médico que já tem conta aceita o convite no painel (menu Clínicas).
    doctorWithAccount() {
      return this.isDoctor && this.existingAccount;
    },
    // Conta de equipe: dados pessoais opcionais (US-C12).
    optionalSuffix() {
      return this.isDoctor ? "" : " (opcional)";
    },
    fieldOrder() {
      if (this.existingAccount) return EXISTING_ACCOUNT_FIELD_ORDER;
      return this.isDoctor ? DOCTOR_FIELD_ORDER : FIELD_ORDER;
    },
    hasContract() {
      return Boolean(this.invite.transfer_pct || this.invite.unit_name);
    },
    // Staff mostra o papel; médico não (a especialidade ele mesmo informa).
    roleLabel() {
      if (this.isDoctor) return "";
      return ROLE_LABELS[this.invite.role] || this.invite.role;
    },
    roleSuffix() {
      return this.roleLabel ? ` como ${this.roleLabel}` : "";
    }
  }
};
</script>

<style scoped>
.invite-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-gray-50, #f7f8fa);
  padding: var(--space-6, 24px);
}
.invite-card {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: var(--radius-lg, 16px);
  box-shadow: var(--shadow-md, 0 4px 24px rgba(0, 0, 0, 0.08));
  padding: var(--space-8, 32px);
}
.vm-logo {
  display: block;
  height: 28px;
  width: auto;
  margin-bottom: var(--space-6, 24px);
}
.invite-title {
  font-size: var(--font-size-xl, 20px);
  font-weight: var(--fw-extra, 800);
  color: var(--color-gray-900, #101828);
}
.invite-muted {
  color: var(--color-gray-500, #667085);
  margin: var(--space-2, 8px) 0 var(--space-5, 20px);
}
.invite-panel-link {
  display: block;
  margin-top: var(--space-6);
  text-align: center;
}

.invite-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4, 16px);
}
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1, 4px);
  margin-bottom: var(--space-4, 16px);
}
.invite-form .field {
  margin-bottom: 0;
}
.field-hint {
  font-size: var(--font-size-xs, 12px);
  color: var(--color-gray-400, #98a2b3);
}
.input--invalid {
  border-color: var(--color-danger, #e5484d) !important;
}
.input:disabled {
  background: var(--color-gray-50, #f7f8fa);
  color: var(--color-gray-500, #667085);
}
.field-error {
  font-size: var(--font-size-xs, 12px);
  color: var(--color-danger, #e5484d);
}
.invite-banner {
  background: var(--color-danger-light, #fdeceb);
  border: 1px solid var(--color-danger, #e5484d);
  border-radius: var(--radius-md, 10px);
  padding: var(--space-3, 12px) var(--space-4, 16px);
  margin-bottom: var(--space-5, 20px);
}
.invite-banner p {
  color: var(--color-danger, #e5484d);
  font-size: var(--font-size-sm, 14px);
  margin: 0;
}
.label-optional {
  color: var(--color-gray-400, #98a2b3);
  font-weight: var(--fw-regular, 400);
}
.invite-textarea {
  height: auto;
  min-height: 88px;
  padding: var(--space-3, 12px) var(--space-4, 16px);
  resize: vertical;
  font-family: inherit;
}
.invite-contract {
  background: var(--color-primary-light, #eef4ff);
  border-radius: var(--radius-md, 10px);
  padding: var(--space-3, 12px) var(--space-4, 16px);
  margin-bottom: var(--space-5, 20px);
}
.invite-contract__line {
  font-size: var(--font-size-sm, 14px);
  color: var(--color-gray-700, #344054);
  margin: 0 0 var(--space-1, 4px);
}
.invite-contract__note {
  font-size: var(--font-size-xs, 12px);
  color: var(--color-gray-500, #667085);
  margin: var(--space-2, 8px) 0 0;
}
.pw-rules {
  list-style: none;
  padding: 0;
  margin: var(--space-1, 4px) 0 0;
  display: grid;
  gap: 2px;
}
.pw-rules li {
  font-size: var(--font-size-xs, 12px);
  color: var(--color-gray-400, #98a2b3);
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
}
.pw-rules li.ok {
  color: var(--color-success, #16a34a);
}
.pw-mark {
  width: 12px;
  display: inline-block;
  text-align: center;
  font-weight: var(--fw-bold, 700);
}
/* BaseInput (senha com toggle do login) no visual do .input da clínica */
.field ::v-deep .v-input-control {
  height: 46px;
  padding: 0 3.5rem 0 var(--space-4, 16px);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-gray-800);
  background: #fff;
  border: 1.5px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
}
.field ::v-deep .v-input-control:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-light);
}
</style>

<style lang="scss">
// Esta tela não usa o layout Base (que importa @/scss inteiro); traz só o que
// o BaseInput de senha e o ícone do olho precisam.
@import "~@/scss/base/variables";
@import "~@/scss/components/input";
@import "~@/scss/components/icons";
</style>
